<template>
  <div class="mobile-spread-viewer" :class="landscape ? 'landscape' : 'portrait'" :style="rootStyle"
       @pointerdown="onPointerDown">
    <!-- Upright phone: one page fills the width and you glide across the spread. -->
    <template v-if="!landscape">
      <div ref="stage" class="stage">
        <div class="strip" :class="transitionClass" :style="stripStyle">
          <div v-for="side of ['left', 'right']" :key="side" class="half" :class="side">
            <page-image v-if="spread[side]" :key="spread[side].id" :page="spread[side]" :reveal="isRevealed(spread[side])"
                        @load="onPageImageLoad" @aspect-ratio="onAspectRatio">
              <page-link-areas :page="spread[side]" />
            </page-image>
            <div v-else class="blank-page" />
          </div>
        </div>
        <div class="hint" :class="{ shown: showHint }">Swipe to move across the spread</div>
      </div>
      <div class="controls">
        <button class="nav" :disabled="spreadIndex === 0" @click="goToSpread(spreadIndex - 1, 'right')">&lt;</button>
        <div ref="minimap" class="minimap" @click="onMinimapClick">
          <img v-for="side of ['left', 'right']" :key="side + (spread[side] ? spread[side].id : '')" :src="thumbnailFor(spread[side])" alt="">
          <div class="minimap-view" :style="minimapViewStyle" />
        </div>
        <button class="nav" :disabled="spreadIndex >= spreadCount - 1" @click="goToSpread(spreadIndex + 1, 'left')">&gt;</button>
      </div>
      <div class="status">
        <span>Spread {{spreadIndex + 1}} of {{spreadCount}} · {{positionLabel}}</span>
        <button class="zoom" @click="toggleZoom">{{mode === 'focus' ? 'Whole spread' : 'Zoom in'}}</button>
      </div>
    </template>

    <!-- Sideways phone: the whole spread, like an open book. -->
    <template v-else>
      <div ref="book" class="book" :style="bookStyle">
        <div v-for="side of ['left', 'right']" :key="side" class="half" :class="side">
          <page-image v-if="bookPages[side]" :key="bookPages[side].id" :page="bookPages[side]"
                      :reveal="flip ? true : isRevealed(bookPages[side])" @load="onPageImageLoad" @aspect-ratio="onAspectRatio">
            <page-link-areas :page="bookPages[side]" />
          </page-image>
          <div v-else class="blank-page" />
        </div>
        <div v-if="flip" ref="leaf" class="leaf" :class="[flip.dir > 0 ? 'forward' : 'backward', { turning: flipTurning }]">
          <div v-for="face of ['front', 'back']" :key="face" class="face" :class="face">
            <page-image v-if="flip[face]" :page="flip[face]" />
            <div v-else class="blank-page" />
          </div>
        </div>
        <div class="gutter" />
      </div>
      <div class="pill" :class="{ shown: showPill }">{{album.name}} · {{spreadIndex + 1}} / {{spreadCount}}</div>
    </template>

    <link v-for="page of prefetchPages" :key="page.id" rel="prefetch" :href="page.pictureUrl">
  </div>
</template>

<script>
import PageImage from './PageImage'
import PageLinkAreas from './PageLinkAreas'

// How long the whole spread is shown before zooming in to a page.
const INTRO_DELAY = 900
const FLIP_DURATION = 800
const CONTROLS_HEIGHT = 90
const DRAG_THRESHOLD = 8
const SWIPE_THRESHOLD = 40

const isLandscape = () => window.innerWidth > window.innerHeight
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
// Don't fight the browser when the user has pinch-zoomed the page.
const isPinchZoomed = () => window.visualViewport && window.visualViewport.scale > 1.01

export default {
  props: ['album', 'pages', 'currentPageIndex', 'setCurrentPageIndex'],
  data() {
    return {
      landscape: isLandscape(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      // Portrait stage size and the viewer's offset from the top of the document.
      width: 0,
      height: 0,
      top: 0,
      // 'overview' shows the whole spread; 'focus' zooms in so a page fills the width.
      mode: 'overview',
      // In focus mode, how far across the spread we are, from 0 (left page) to width (right page).
      pan: 0,
      transitionClass: null,
      // Used to slide a new spread in from the side.
      enterOffset: 0,
      pendingLanding: 'left',
      pendingEnterDirection: 0,
      introTimer: null,
      showHint: false,
      hintShown: false,
      showPill: false,
      pillTimer: null,
      flip: null,
      flipTurning: false,
      drag: null,
      aspectRatios: {},
      loadedPageIds: {},
    }
  },
  computed: {
    spreadIndex() {
      return Math.floor(this.currentPageIndex / 2)
    },
    spreadCount() {
      return Math.ceil(this.pages.length / 2)
    },
    spread() {
      return this.spreadAt(this.spreadIndex)
    },
    aspectRatio() {
      const page = this.spread.left || this.spread.right
      return (page && this.aspectRatios[page.id]) || 1
    },
    spreadLoaded() {
      return [this.spread.left, this.spread.right].filter(Boolean).every(page => this.loadedPageIds[page.id])
    },
    rootStyle() {
      if (this.landscape) {
        return {}
      }
      return { height: Math.max(320, this.viewportHeight - this.top) + 'px' }
    },
    pageHeight() {
      return this.width / this.aspectRatio
    },
    scale() {
      const fitHeight = this.height / this.pageHeight
      return this.mode === 'focus' ? Math.min(1, fitHeight) : Math.min(0.5, fitHeight)
    },
    // The point on the spread (in unscaled pixels) that's centered on screen.
    centerX() {
      return this.mode === 'focus' ? this.pan + this.width / 2 : this.width
    },
    stripStyle() {
      const x = this.width / 2 - this.centerX * this.scale + this.enterOffset
      const y = (this.height - this.pageHeight * this.scale) / 2
      return {
        width: (this.width * 2) + 'px',
        height: this.pageHeight + 'px',
        transform: `translate(${x}px, ${y}px) scale(${this.scale})`,
      }
    },
    minimapViewStyle() {
      const visibleWidth = Math.min(this.width * 2, this.width / this.scale)
      const left = Math.max(0, this.centerX - visibleWidth / 2)
      return {
        left: (100 * left / (this.width * 2)) + '%',
        width: (100 * visibleWidth / (this.width * 2)) + '%',
      }
    },
    positionLabel() {
      if (this.mode === 'overview') {
        return 'Whole spread'
      }
      if (this.pan < this.width * 0.25) {
        return 'Left page'
      }
      if (this.pan > this.width * 0.75) {
        return 'Right page'
      }
      return 'Both pages'
    },
    bookStyle() {
      const margin = 8
      const availableWidth = this.viewportWidth - margin * 2
      const availableHeight = this.viewportHeight - margin * 2
      const height = Math.min(availableHeight, availableWidth / (2 * this.aspectRatio))
      const width = height * 2 * this.aspectRatio
      return {
        width: width + 'px',
        height: height + 'px',
        left: ((this.viewportWidth - width) / 2) + 'px',
        top: ((this.viewportHeight - height) / 2) + 'px',
      }
    },
    // While a page is turning, the pages under the turning leaf are a mix of the old and new spreads.
    bookPages() {
      if (!this.flip) {
        return this.spread
      }
      const { dir, from, to } = this.flip
      return dir > 0 ? { left: from.left, right: to.right } : { left: to.left, right: from.right }
    },
    prefetchPages() {
      return [this.spreadIndex - 1, this.spreadIndex + 1]
        .map(i => this.spreadAt(i))
        .reduce((a, spread) => a.concat([spread.left, spread.right]), [])
        .filter(Boolean)
    },
  },
  methods: {
    spreadAt(index) {
      return {
        left: this.pages[index * 2] || null,
        right: this.pages[index * 2 + 1] || null,
      }
    },
    thumbnailFor(page) {
      return page ? (page.thumbnailUrl || page.pictureUrl) : ''
    },
    // Pages without a thumbnail have nothing to show while waiting, so show them as soon as they load.
    isRevealed(page) {
      return this.spreadLoaded || !page.thumbnailUrl
    },
    onPageImageLoad(page) {
      this.$set(this.loadedPageIds, page.id, true)
    },
    onAspectRatio({ page, aspectRatio }) {
      this.$set(this.aspectRatios, page.id, aspectRatio)
    },
    measure() {
      const oldWidth = this.width
      this.landscape = isLandscape()
      this.viewportWidth = window.innerWidth
      this.viewportHeight = window.innerHeight
      document.documentElement.classList.toggle('mobile-spread-viewer-landscape', this.landscape)
      if (this.landscape) {
        return
      }
      this.top = this.$el.getBoundingClientRect().top + window.pageYOffset
      this.width = this.$el.clientWidth
      this.height = Math.max(200, Math.max(320, this.viewportHeight - this.top) - CONTROLS_HEIGHT)
      if (oldWidth) {
        this.pan = this.pan / oldWidth * this.width
      }
    },
    onResize() {
      const wasLandscape = this.landscape
      this.transitionClass = null
      this.measure()
      if (wasLandscape !== this.landscape) {
        // Measure again once the other layout has rendered.
        this.$nextTick(this.measure)
        if (this.landscape) {
          this.flashPill()
        }
      }
    },
    clearIntro() {
      clearTimeout(this.introTimer)
      this.introTimer = null
    },
    // Show the whole spread briefly so the viewer sees the layout, then zoom in.
    openSpread(landOn, enterDirection) {
      this.clearIntro()
      const landingPan = landOn === 'right' ? this.width : 0
      if (prefersReducedMotion()) {
        this.transitionClass = null
        this.mode = landOn ? 'focus' : 'overview'
        this.pan = landingPan
        return
      }
      this.transitionClass = null
      this.mode = 'overview'
      this.enterOffset = enterDirection * this.width
      // Wait a frame so the starting position is painted before animating.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.transitionClass = 'animate'
        this.enterOffset = 0
      }))
      if (!landOn) {
        return
      }
      this.introTimer = setTimeout(() => {
        this.introTimer = null
        this.transitionClass = 'animate-slow'
        this.mode = 'focus'
        this.pan = landingPan
        if (!this.hintShown) {
          this.hintShown = true
          setTimeout(() => { this.showHint = true }, 1000)
          setTimeout(() => { this.showHint = false }, 3500)
        }
      }, INTRO_DELAY)
    },
    // landOn is 'left', 'right', or null to stay zoomed out.
    goToSpread(index, landOn) {
      if (index < 0 || index >= this.spreadCount || index === this.spreadIndex) {
        return
      }
      this.pendingLanding = landOn
      this.pendingEnterDirection = index > this.spreadIndex ? 1 : -1
      this.setCurrentPageIndex(index * 2)
    },
    snapPan(target) {
      this.transitionClass = 'animate'
      this.mode = 'focus'
      this.pan = target
    },
    nearestSnap(pan) {
      return [0, this.width / 2, this.width].reduce((a, b) => Math.abs(b - pan) < Math.abs(a - pan) ? b : a)
    },
    toggleZoom() {
      this.clearIntro()
      this.transitionClass = 'animate'
      this.mode = this.mode === 'focus' ? 'overview' : 'focus'
    },
    next() {
      if (this.landscape) {
        return this.turn(1)
      }
      if (this.mode === 'focus' && this.pan < this.width - 1) {
        return this.snapPan(this.pan < this.width / 2 - 1 ? this.width / 2 : this.width)
      }
      this.goToSpread(this.spreadIndex + 1, this.mode === 'focus' ? 'left' : null)
    },
    previous() {
      if (this.landscape) {
        return this.turn(-1)
      }
      if (this.mode === 'focus' && this.pan > 1) {
        return this.snapPan(this.pan > this.width / 2 + 1 ? this.width / 2 : 0)
      }
      this.goToSpread(this.spreadIndex - 1, this.mode === 'focus' ? 'right' : null)
    },
    onMinimapClick(event) {
      this.clearIntro()
      const rect = this.$refs.minimap.getBoundingClientRect()
      const fraction = (event.clientX - rect.left) / rect.width
      this.snapPan(this.nearestSnap(fraction * this.width * 2 - this.width / 2))
    },
    onPointerDown(event) {
      if (event.button || isPinchZoomed() || this.flip || event.target.closest('button, .minimap')) {
        return
      }
      this.clearIntro()
      this.drag = { x: event.clientX, y: event.clientY, time: Date.now(), startPan: this.pan, axis: null }
      window.addEventListener('pointermove', this.onPointerMove)
      window.addEventListener('pointerup', this.onPointerUp)
      window.addEventListener('pointercancel', this.onPointerCancel)
    },
    onPointerMove(event) {
      const drag = this.drag
      const dx = event.clientX - drag.x
      const dy = event.clientY - drag.y
      if (!drag.axis && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      }
      if (drag.axis !== 'x' || this.landscape || this.mode !== 'focus') {
        return
      }
      // Follow the finger, with resistance past either end of the spread.
      const raw = drag.startPan - dx
      const max = this.width
      this.transitionClass = null
      this.showHint = false
      this.pan = raw < 0 ? raw / 3 : raw > max ? max + (raw - max) / 3 : raw
    },
    onPointerUp(event) {
      const drag = this.drag
      this.removePointerListeners()
      const dx = event.clientX - drag.x
      if (!drag.axis) {
        return this.onTap(event)
      }
      if (drag.axis !== 'x') {
        return
      }
      if (this.landscape) {
        if (Math.abs(dx) > SWIPE_THRESHOLD) {
          this.turn(dx < 0 ? 1 : -1)
        }
        return
      }
      if (this.mode === 'overview') {
        if (Math.abs(dx) > SWIPE_THRESHOLD) {
          this.goToSpread(this.spreadIndex + (dx < 0 ? 1 : -1), null)
        }
        return
      }
      const raw = drag.startPan - dx
      if (raw > this.width * 1.25 && this.spreadIndex < this.spreadCount - 1) {
        return this.goToSpread(this.spreadIndex + 1, 'left')
      }
      if (raw < -this.width * 0.25 && this.spreadIndex > 0) {
        return this.goToSpread(this.spreadIndex - 1, 'right')
      }
      // A quick flick moves to the next resting point even if the finger didn't travel far.
      const velocity = dx / Math.max(1, Date.now() - drag.time)
      let target = this.nearestSnap(this.pan)
      if (Math.abs(velocity) > 0.5 && target === this.nearestSnap(drag.startPan)) {
        const step = this.width / 2
        target = Math.min(this.width, Math.max(0, target + (velocity < 0 ? step : -step)))
      }
      this.snapPan(target)
    },
    onPointerCancel() {
      this.removePointerListeners()
      if (!this.landscape && this.mode === 'focus') {
        this.snapPan(this.nearestSnap(this.pan))
      }
    },
    removePointerListeners() {
      this.drag = null
      window.removeEventListener('pointermove', this.onPointerMove)
      window.removeEventListener('pointerup', this.onPointerUp)
      window.removeEventListener('pointercancel', this.onPointerCancel)
    },
    onTap(event) {
      // Let taps on page links through.
      if (event.target.closest('a')) {
        return
      }
      if (this.landscape) {
        const rect = this.$refs.book.getBoundingClientRect()
        return this.turn(event.clientX > rect.left + rect.width / 2 ? 1 : -1)
      }
      this.transitionClass = 'animate'
      if (this.mode === 'focus') {
        this.mode = 'overview'
      } else {
        const rect = this.$refs.stage.getBoundingClientRect()
        this.mode = 'focus'
        this.pan = event.clientX - rect.left < rect.width / 2 ? 0 : this.width
      }
    },
    turn(dir) {
      const toIndex = this.spreadIndex + dir
      if (this.flip || toIndex < 0 || toIndex >= this.spreadCount) {
        return
      }
      const from = this.spread
      const to = this.spreadAt(toIndex)
      const finish = () => {
        this.pendingLanding = null
        this.pendingEnterDirection = 0
        this.setCurrentPageIndex(toIndex * 2)
        this.flip = null
        this.flipTurning = false
        this.flashPill()
      }
      if (prefersReducedMotion()) {
        return finish()
      }
      // The leaf's front is the page being turned; its back is the page it reveals on the other side.
      this.flip = dir > 0
        ? { dir, from, to, front: from.right, back: to.left }
        : { dir, from, to, front: from.left, back: to.right }
      this.flipTurning = false
      this.$nextTick(() => {
        // Force layout so the turn animates from flat.
        this.$refs.leaf.getBoundingClientRect()
        this.flipTurning = true
        setTimeout(finish, FLIP_DURATION)
      })
    },
    flashPill() {
      this.showPill = true
      clearTimeout(this.pillTimer)
      this.pillTimer = setTimeout(() => { this.showPill = false }, 2500)
    },
    onKeydown(event) {
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return
      }
      if (event.code === 'ArrowRight') {
        this.next()
      } else if (event.code === 'ArrowLeft') {
        this.previous()
      }
    },
  },
  watch: {
    spreadIndex() {
      if (this.landscape) {
        return
      }
      const landOn = this.pendingLanding
      const enterDirection = this.pendingEnterDirection
      this.pendingLanding = 'left'
      this.pendingEnterDirection = 0
      this.openSpread(landOn, enterDirection)
    },
    landscape(landscape) {
      if (!landscape) {
        this.flip = null
        this.$nextTick(() => {
          this.measure()
          this.openSpread('left', 0)
        })
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    window.addEventListener('keydown', this.onKeydown)
    this.measure()
    if (this.landscape) {
      this.flashPill()
    } else {
      this.openSpread('left', 0)
    }
  },
  beforeDestroy() {
    this.clearIntro()
    clearTimeout(this.pillTimer)
    this.removePointerListeners()
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('keydown', this.onKeydown)
    document.documentElement.classList.remove('mobile-spread-viewer-landscape')
  },
  components: {
    PageImage,
    PageLinkAreas,
  },
}
</script>

<style lang="stylus">
// Sideways, the viewer covers the whole screen, so stop the page behind it from scrolling.
html.mobile-spread-viewer-landscape
  &, body
    overflow hidden
</style>

<style lang="stylus" scoped>
paper-color = #f3efe8

.mobile-spread-viewer
  user-select none
  -webkit-user-select none
  -webkit-touch-callout none
  // Let the browser handle vertical scrolling and pinch zoom; we handle horizontal swipes.
  touch-action pan-y pinch-zoom

.portrait
  display flex
  flex-direction column

  .stage
    position relative
    flex 1
    overflow hidden

  .strip
    position absolute
    top 0
    left 0
    transform-origin 0 0
    will-change transform
    box-shadow 0 6px 18px rgba(0, 0, 0, .25)

    &.animate
      transition transform .45s cubic-bezier(.22, .8, .26, 1)

    &.animate-slow
      transition transform .9s cubic-bezier(.45, 0, .2, 1)

    // Shadow in the gutter so it reads as an open book.
    &::after
      content ''
      position absolute
      top 0
      bottom 0
      left 50%
      width 6%
      transform translateX(-50%)
      background linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, .15) 48%, rgba(0, 0, 0, .25) 50%, rgba(0, 0, 0, .15) 52%, rgba(0, 0, 0, 0))
      pointer-events none

  .half
    position absolute
    top 0
    width 50%
    height 100%

    &.left
      left 0

    &.right
      left 50%

  .hint
    position absolute
    left 50%
    top 50%
    transform translate(-50%, -50%)
    background rgba(20, 20, 20, .75)
    color white
    font-size 14px
    padding 6px 14px
    border-radius 99px
    white-space nowrap
    pointer-events none
    opacity 0
    transition opacity .4s

    &.shown
      opacity 1

  .controls
    display flex
    align-items center
    justify-content center
    padding-top 10px

    button.nav
      margin 0 12px
      width 36px
      height 36px
      border-radius 50%
      padding 0
      font-size 16px

  .minimap
    position relative
    display flex
    width 45%
    max-width 220px
    cursor pointer
    box-shadow 0 2px 6px rgba(0, 0, 0, .25)

    img
      width 50%
      aspect-ratio 1
      object-fit cover
      background paper-color

  .minimap-view
    position absolute
    top -3px
    bottom -3px
    border 2px solid #189ea6
    border-radius 3px
    transition left .35s ease, width .35s ease
    pointer-events none

  .status
    display flex
    justify-content center
    align-items center
    font-size 13px
    color #555
    padding 6px 0

    button.zoom
      margin 0 0 0 10px
      padding 4px 10px
      font-size 11px

.blank-page
  width 100%
  height 100%
  background paper-color

.landscape
  position fixed
  top 0
  right 0
  bottom 0
  left 0
  z-index 50
  background #2a2420

  .book
    position absolute
    perspective 2000px
    box-shadow 0 4px 0 #d8d0c4, 0 6px 0 #c9bfb1, 0 10px 24px rgba(0, 0, 0, .5)

  .half
    position absolute
    top 0
    width 50%
    height 100%

    &.left
      left 0

    &.right
      left 50%

  .leaf
    position absolute
    top 0
    width 50%
    height 100%
    z-index 2
    transform-style preserve-3d
    transition transform .8s cubic-bezier(.4, .1, .25, 1)

    &.forward
      left 50%
      transform-origin 0 50%

      &.turning
        transform rotateY(-180deg)

    &.backward
      left 0
      transform-origin 100% 50%

      &.turning
        transform rotateY(180deg)

  .face
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    backface-visibility hidden
    -webkit-backface-visibility hidden

    &.back
      transform rotateY(180deg)

  .gutter
    position absolute
    top 0
    bottom 0
    left 50%
    width 8%
    transform translateX(-50%)
    z-index 3
    pointer-events none
    background linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, .18) 46%, rgba(0, 0, 0, .32) 50%, rgba(0, 0, 0, .18) 54%, rgba(0, 0, 0, 0))

  .pill
    position absolute
    left 50%
    bottom 10px
    transform translateX(-50%)
    z-index 4
    background rgba(0, 0, 0, .6)
    color white
    font-size 13px
    padding 4px 12px
    border-radius 99px
    white-space nowrap
    pointer-events none
    opacity 0
    transition opacity .5s

    &.shown
      opacity 1
</style>
